import React from 'react';
import {
  IApplicationComponentRef,
  IApplicationParentRef,
  IApplicationResult,
  IApplicationScopeRef,
  IReferenceValue,
  ITag,
  ReducerActionMap,
} from '../types';

export enum AdminTypes {
  SELECT_APPLICATION = 'SELECT_APPLICATION',
  SELECT_LEVEL = 'SELECT_LEVEL',
  SELECT_RESULT = 'SELECT_RESULT',
  SELECT_REFERENCE_ID = 'SELECT_REFERENCE_ID',
  SELECT_PARENT = 'SELECT_PARENT',
  SELECT_SCOPE = 'SELECT_SCOPE',
  SELECT_COMPONENT = 'SELECT_COMPONENT',
  SET_APPLICATIONS = 'SET_APPLICATIONS',
  SET_REFERENCES_NAME = 'SET_REFERENCES_NAME',
  SET_RESULTS = 'SET_RESULTS',
  SET_RESULT_VALUE = 'SET_RESULT_VALUE',
  SET_RESULT_COMMENT = 'SET_RESULT_COMMENT',
  SET_PARENTS = 'SET_PARENTS',
  SET_SCOPES = 'SET_SCOPES',
  SET_COMPONENTS = 'SET_COMPONENTS',
  SET_TAGS = 'SET_TAGS',
}

type AdminPayload = {
  [AdminTypes.SELECT_APPLICATION]: { applicationName: string };
  [AdminTypes.SELECT_LEVEL]: { level: string };
  [AdminTypes.SELECT_RESULT]: { result: IApplicationResult };
  [AdminTypes.SELECT_REFERENCE_ID]: { referenceId: number };
  [AdminTypes.SELECT_PARENT]: { parent: string };
  [AdminTypes.SELECT_SCOPE]: { scope: string };
  [AdminTypes.SELECT_COMPONENT]: { component: string };
  [AdminTypes.SET_APPLICATIONS]: { applicationsName: string[] };
  [AdminTypes.SET_REFERENCES_NAME]: { referencesValue: IReferenceValue[] };
  [AdminTypes.SET_RESULTS]: { results: IApplicationResult[] };
  [AdminTypes.SET_RESULT_VALUE]: { value: string };
  [AdminTypes.SET_RESULT_COMMENT]: { comment: string };
  [AdminTypes.SET_PARENTS]: { parents: IApplicationParentRef[] };
  [AdminTypes.SET_SCOPES]: { scopesRef: IApplicationScopeRef[] };
  [AdminTypes.SET_COMPONENTS]: { components: IApplicationComponentRef[] };
  [AdminTypes.SET_TAGS]: { tags: ITag[] };
};

type AdminActions = ReducerActionMap<AdminPayload>[keyof ReducerActionMap<AdminPayload>];

type InitialStateType = {
  applicationsName: string[] | undefined;
  referencesValue: IReferenceValue[] | undefined;
  results: IApplicationResult[] | undefined;
  value: string | undefined;
  comment: string | undefined;
  selectedApplicationName: string | undefined;
  selectedLevel: string | undefined;
  selectedReferenceId: number | undefined;
  selectedResult: IApplicationResult | undefined;
  parents: IApplicationParentRef[] | undefined;
  scopesRef: IApplicationScopeRef[] | undefined;
  components: IApplicationComponentRef[] | undefined;
  selectedParentKey: string | undefined;
  selectedScopeKey: string | undefined;
  selectedComponentKey: string | undefined;
  tags: ITag[] | undefined;
};

const initialState: InitialStateType = {
  applicationsName: undefined,
  referencesValue: undefined,
  results: undefined,
  value: undefined,
  comment: undefined,
  selectedApplicationName: undefined,
  selectedLevel: undefined,
  selectedReferenceId: undefined,
  selectedResult: undefined,
  parents: undefined,
  scopesRef: undefined,
  components: undefined,
  selectedParentKey: undefined,
  selectedScopeKey: undefined,
  selectedComponentKey: undefined,
  tags: undefined,
};

const AdminContext = React.createContext<
  | {
      state: InitialStateType;
    }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  | any
>({
  state: initialState,
});

AdminContext.displayName = 'AdminContext';

export const adminReducer = (state: InitialStateType, action: AdminActions): InitialStateType => {
  switch (action.type) {
    case AdminTypes.SELECT_APPLICATION: {
      const { applicationName } = action.payload;

      return {
        ...state,
        selectedApplicationName: applicationName,
      };
    }
    case AdminTypes.SELECT_RESULT: {
      const { result } = action.payload;

      return {
        ...state,
        selectedResult: result,
      };
    }
    case AdminTypes.SELECT_LEVEL: {
      const { level } = action.payload;

      return {
        ...state,
        selectedLevel: level,
      };
    }
    case AdminTypes.SELECT_REFERENCE_ID: {
      const { referenceId } = action.payload;

      return {
        ...state,
        selectedReferenceId: referenceId,
      };
    }
    case AdminTypes.SELECT_PARENT: {
      const { parent } = action.payload;

      return {
        ...state,
        selectedParentKey: parent,
      };
    }
    case AdminTypes.SELECT_SCOPE: {
      const { scope } = action.payload;

      return {
        ...state,
        selectedScopeKey: scope,
      };
    }
    case AdminTypes.SELECT_COMPONENT: {
      const { component } = action.payload;

      return {
        ...state,
        selectedComponentKey: component,
      };
    }
    case AdminTypes.SET_APPLICATIONS: {
      const { applicationsName } = action.payload;

      return {
        ...state,
        applicationsName,
      };
    }
    case AdminTypes.SET_REFERENCES_NAME: {
      const { referencesValue } = action.payload;

      return {
        ...state,
        referencesValue,
      };
    }
    case AdminTypes.SET_RESULTS: {
      const { results } = action.payload;

      return {
        ...state,
        results,
      };
    }
    case AdminTypes.SET_RESULT_VALUE: {
      const { value } = action.payload;

      return {
        ...state,
        value,
      };
    }
    case AdminTypes.SET_RESULT_COMMENT: {
      const { comment } = action.payload;

      return {
        ...state,
        comment,
      };
    }
    case AdminTypes.SET_PARENTS: {
      const { parents } = action.payload;

      return {
        ...state,
        parents,
      };
    }
    case AdminTypes.SET_SCOPES: {
      const { scopesRef } = action.payload;

      return {
        ...state,
        scopesRef,
      };
    }
    case AdminTypes.SET_TAGS: {
      const { tags } = action.payload;

      return {
        ...state,
        tags,
      };
    }
    case AdminTypes.SET_COMPONENTS: {
      const { components } = action.payload;

      return {
        ...state,
        components,
      };
    }
    default: {
      return state;
    }
  }
};

const AdminProvider: React.FunctionComponent<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = React.useReducer(adminReducer, initialState);

  const setApplicationsName = React.useCallback(
    (applicationsName: string[]) => dispatch({ type: AdminTypes.SET_APPLICATIONS, payload: { applicationsName } }),
    [dispatch]
  );

  const setReferencesValue = React.useCallback(
    (referencesValue: IReferenceValue[]) =>
      dispatch({ type: AdminTypes.SET_REFERENCES_NAME, payload: { referencesValue } }),
    [dispatch]
  );

  const getSelectedApplicationName = React.useCallback(
    (applicationName: string) => dispatch({ type: AdminTypes.SELECT_APPLICATION, payload: { applicationName } }),
    [dispatch]
  );

  const getSelectedLevel = React.useCallback(
    (level: string) => dispatch({ type: AdminTypes.SELECT_LEVEL, payload: { level } }),
    [dispatch]
  );

  const getSelectedReference = React.useCallback(
    (referenceId: number) => dispatch({ type: AdminTypes.SELECT_REFERENCE_ID, payload: { referenceId } }),
    [dispatch]
  );

  const getSelectedResult = React.useCallback(
    (result: IApplicationResult) => dispatch({ type: AdminTypes.SELECT_RESULT, payload: { result } }),
    [dispatch]
  );

  const setSelectedResult = React.useCallback(
    (results: IApplicationResult[]) => dispatch({ type: AdminTypes.SET_RESULTS, payload: { results } }),
    [dispatch]
  );

  const setUpdatedValue = React.useCallback(
    (value: string) => dispatch({ type: AdminTypes.SET_RESULT_VALUE, payload: { value } }),
    [dispatch]
  );

  const setUpdatedComment = React.useCallback(
    (comment: string) => dispatch({ type: AdminTypes.SET_RESULT_COMMENT, payload: { comment } }),
    [dispatch]
  );

  const setParentsRef = React.useCallback(
    (parents: IApplicationParentRef[]) => dispatch({ type: AdminTypes.SET_PARENTS, payload: { parents } }),
    [dispatch]
  );

  const setScopesRef = React.useCallback(
    (scopesRef: IApplicationScopeRef[]) => dispatch({ type: AdminTypes.SET_SCOPES, payload: { scopesRef } }),
    [dispatch]
  );

  const setTags = React.useCallback(
    (tags: ITag[]) => dispatch({ type: AdminTypes.SET_TAGS, payload: { tags } }),
    [dispatch]
  );

  const setComponentsRef = React.useCallback(
    (components: IApplicationComponentRef[]) => dispatch({ type: AdminTypes.SET_COMPONENTS, payload: { components } }),
    [dispatch]
  );

  const setSelectedParentKey = React.useCallback(
    (parent: string) => dispatch({ type: AdminTypes.SELECT_PARENT, payload: { parent } }),
    [dispatch]
  );

  const setSelectedScopeKey = React.useCallback(
    (scope: string) => dispatch({ type: AdminTypes.SELECT_SCOPE, payload: { scope } }),
    [dispatch]
  );

  const setSelectedComponentKey = React.useCallback(
    (component: string) => dispatch({ type: AdminTypes.SELECT_COMPONENT, payload: { component } }),
    [dispatch]
  );

  const value = React.useMemo(
    () => ({
      ...state,
      setApplicationsName,
      setReferencesValue,
      setSelectedResult,
      setUpdatedValue,
      setUpdatedComment,
      setParentsRef,
      setScopesRef,
      setTags,
      setComponentsRef,
      getSelectedApplicationName,
      getSelectedLevel,
      getSelectedReference,
      getSelectedResult,
      setSelectedParentKey,
      setSelectedScopeKey,
      setSelectedComponentKey,
    }),
    [
      state,
      setApplicationsName,
      setReferencesValue,
      setSelectedResult,
      setUpdatedValue,
      setUpdatedComment,
      setParentsRef,
      setScopesRef,
      setTags,
      setComponentsRef,
      getSelectedApplicationName,
      getSelectedLevel,
      getSelectedReference,
      getSelectedResult,
      setSelectedParentKey,
      setSelectedScopeKey,
      setSelectedComponentKey,
    ]
  );

  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
};

export const useAdminState = (): {
  setApplicationsName: (applicationsName: string[]) => void;
  setReferencesValue: (referencesValue: IReferenceValue[] | undefined) => void;
  setSelectedResult: (results: IApplicationResult[]) => void;
  setUpdatedValue: (value: string) => void;
  setUpdatedComment: (comment: string) => void;
  setParentsRef: (parents: IApplicationParentRef[]) => void;
  setScopesRef: (scopesRef: IApplicationScopeRef[] | undefined) => void;
  setComponentsRef: (components: IApplicationComponentRef[] | undefined) => void;
  getSelectedApplicationName: (applicationName: string) => void;
  getSelectedLevel: (level: string | undefined) => void;
  getSelectedReference: (referenceId: number | undefined) => void;
  getSelectedResult: (result: IApplicationResult) => void;
  setSelectedParentKey: (parent: string | undefined) => void;
  setSelectedScopeKey: (scope: string | undefined) => void;
  setSelectedComponentKey: (scope: string | undefined) => void;
  setTags: (tags: ITag[] | undefined) => void;
} & InitialStateType => {
  const context = React.useContext(AdminContext);
  if (context === undefined) {
    throw new Error(`useAdmin must be used within an Admin Provider`);
  }
  return context;
};

export { AdminContext, AdminProvider };
